import React from "react";
import { Box, Flex, Icon, Text, useColorModeValue } from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import formatAmount from "./formatAmount";
import { CHANNEL_LABELS } from "./treasuryConstants";

/**
 * Renders one cross-currency flow: money cashed in on one rail while the debt
 * it settles lives on the other. This is what a rebalancing transfer must undo.
 * @param {Object} props - a cross_currency entry from the API
 * @returns {React.ReactElement}
 */
function FlowRow({ flow }) {
    const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="12px" p="16px" mb="12px">
            <Flex align="center" gap="10px" wrap="wrap" mb="10px">
                <Text fontSize="sm" fontWeight="700" color={valueColor}>
                    {CHANNEL_LABELS[flow.collected_channel] || flow.collected_channel}
                </Text>
                <Icon as={MdArrowForward} color={labelColor} />
                <Text fontSize="sm" fontWeight="700" color={valueColor}>
                    {CHANNEL_LABELS[flow.owed_channel] || flow.owed_channel}
                </Text>
                <Text fontSize="xs" color={labelColor}>
                    · {flow.payments} paiement{flow.payments > 1 ? "s" : ""}
                </Text>
            </Flex>
            <Text fontSize="sm" color={labelColor}>
                Encaissé{" "}
                <Text as="span" fontWeight="700" color={valueColor}>
                    {formatAmount(flow.collected_amount, flow.collected_currency)}
                </Text>{" "}
                pour une dette de{" "}
                <Text as="span" fontWeight="700" color={valueColor}>
                    {formatAmount(flow.owed_amount, flow.owed_currency)}
                </Text>
                .
            </Text>
        </Box>
    );
}

/**
 * Renders the list of cross-currency flows behind any channel imbalance.
 * @param {Object} props - the cross_currency array from the API
 * @returns {React.ReactElement}
 */
function CrossCurrencyPanel({ flows }) {
    const cardBg = useColorModeValue("white", "navy.700");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    return (
        <Box bg={cardBg} borderRadius="16px" p="20px" mb="20px">
            <Text fontSize="lg" fontWeight="700" color={titleColor} mb="4px">
                Flux entre canaux
            </Text>
            <Text fontSize="sm" color={hintColor} mb="16px">
                Réservations encaissées sur un canal alors que l'artiste doit être payé
                sur l'autre. C'est ce qui creuse l'écart : chaque ligne indique le sens
                dans lequel un transfert entre les deux comptes serait nécessaire.
            </Text>
            {flows.length === 0 ? (
                <Text fontSize="sm" color={hintColor}>
                    Aucun paiement croisé à ce jour — chaque canal ne porte que ses propres
                    engagements.
                </Text>
            ) : (
                flows.map((flow, index) => (
                    <FlowRow key={`${flow.collected_currency}-${flow.owed_currency}-${index}`} flow={flow} />
                ))
            )}
        </Box>
    );
}

export default CrossCurrencyPanel;
